import { HttpContext, HttpContextToken, HttpInterceptorFn } from '@angular/common/http';

/**
 * Idempotencia anti doble-submit en los POST/PATCH de CREACION/EJECUCION de plata.
 *
 * EL PROBLEMA: un doble-click, un reintento por red lenta o un reenvio del browser
 * puede duplicar una orden de pago o ejecutar un pago dos veces. El backend YA acepta
 * el header `Idempotency-Key` y deduplica por esa clave dentro de una ventana; el
 * front solo tenia proteccion de UX ([disabled] mientras `submitting()`), que NO
 * cubre esos casos de carrera.
 *
 * EL CONTRATO DE LA CLAVE: debe ser ESTABLE por intento logico de operacion y
 * DISTINTA entre operaciones nuevas. Si el interceptor generara una clave nueva en
 * cada disparo, dos clicks producirian dos claves y se perderia la dedupe. Por eso
 * la clave la genera el COMPONENTE/SERVICIO al armar el payload (una vez, al abrir el
 * modal o al primer submit) y la adjunta al request via este HttpContext token. El
 * interceptor solo la lee y la copia al header. Asi un retry del MISMO submit reusa
 * la clave; un submit nuevo trae otra.
 *
 * ALCANCE ACOTADO: solo aplica cuando el request trae la clave en el contexto. NO se
 * toca login ni el resto de los POST. Los call-sites de plata son los unicos que la
 * setean (ver orden-pago / pago / prestamos / compras-divisas / aprobaciones).
 */
export const IDEMPOTENCY_KEY = new HttpContextToken<string | null>(() => null);

/** Genera una clave de idempotencia nueva (un UUID v4 del browser) por intento. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Helper para los servicios: arma un HttpContext con la clave de idempotencia.
 * Uso: `this.http.post(url, body, { context: withIdempotencyKey(key) })`.
 */
export function withIdempotencyKey(key: string, base?: HttpContext): HttpContext {
  return (base ?? new HttpContext()).set(IDEMPOTENCY_KEY, key);
}

export const idempotencyInterceptor: HttpInterceptorFn = (req, next) => {
  const key = req.context.get(IDEMPOTENCY_KEY);
  // Sin clave en el contexto => request comun: no se toca (login, GETs, etc.).
  if (!key) return next(req);

  // Solo mutaciones de creacion/ejecucion. GET nunca lleva idempotencia.
  if (req.method === 'GET' || req.method === 'DELETE') return next(req);

  return next(req.clone({ setHeaders: { 'Idempotency-Key': key } }));
};
